import { useMemo } from 'react';

import { Tooltip } from '@reckon-web/tooltip';
import { Flex } from '@reckon-web/flex';
import { makeId } from '@reckon-web/utils';
import { typedKeys } from '@reckon-web/utils';

import {
  TaskStatusEnum,
  TaskStatusEnumValue,
} from '../work/types/task-status-enum';

import { Tag, TagProps } from './tag';

const taskStatuses: Record<
  TaskStatusEnumValue,
  {
    label: string;
    color: TagProps['color'];
  }
> = {
  [TaskStatusEnum.valuesEnum.NotStarted]: {
    label: 'Not started',
    color: 'gray-light',
  },
  [TaskStatusEnum.valuesEnum.InProgress]: {
    label: 'In progress',
    color: 'blue-light',
  },
  [TaskStatusEnum.valuesEnum.OnHold]: {
    label: 'On hold',
    color: 'purple-light',
  },
  [TaskStatusEnum.valuesEnum.ReadyForReview]: {
    label: 'Ready for review',
    color: 'orange-light',
  },
  [TaskStatusEnum.valuesEnum.Done]: {
    label: 'Done',
    color: 'green-light',
  },
  [TaskStatusEnum.valuesEnum.Cancelled]: {
    label: 'Cancelled',
    color: 'red-light',
  },
} as const;
const taskStatusesKeys = typedKeys(taskStatuses);

export const TaskStatusBadge: React.FC<{
  status: TaskStatusEnumValue;
  testId?: string;
}> = ({ status, testId = 'task-status-badge' }) => {
  const { label, color } = taskStatuses[status];
  return <Tag color={color} label={label} data={{ testid: testId }} />;
};

export const getTaskStatusLabel = (status: TaskStatusEnumValue) =>
  taskStatuses[status].label;

export const TaskStatusBadges: React.FC<{
  tasks: { status?: TaskStatusEnumValue }[];
  testId?: string;
}> = ({ tasks, testId = 'task-status-badges' }) => {
  // build up the count by status for quicker referring to later
  const groupedByStatus = useMemo(
    () =>
      tasks.reduce((result, item) => {
        if (!item.status) {
          return result;
        }
        const existing = result.get(item.status) || 0;
        result.set(item.status, existing + 1);
        return result;
      }, new Map<TaskStatusEnumValue, number>()),
    [tasks]
  );

  return (
    <Flex alignItems="center" gap="xsmall" data={{ testid: testId }}>
      {taskStatusesKeys.map((status) => {
        const { label, color } = taskStatuses[status];

        return (
          <Tooltip key={status} content={label}>
            <Tag
              color={color}
              label={String(groupedByStatus.get(status) || 0)}
              data={{ testid: makeId(testId, status) }}
            />
          </Tooltip>
        );
      })}
    </Flex>
  );
};
